import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Toast from "../../components/Toast";
import { db, auth, functions } from '../../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { useAuth } from '../../AuthContext';
import { useSubAgentAuth } from '../../pages/Clients/SubAgent/SubAgentAuthContext';

// redux
import { useAppSelector } from '../../redux/store';
import getCurrentScreen from '../../Helper/GetScreen';
import GradientSubmitButton from '../GradientSubmitButton';

interface Loan {
  id: string;
  name: string;
  number: string;
  email?: string;
  stageName: string;
  canInitiate: boolean;
  createdAt: any;
}

interface ToastProps {
  message: string;
  isError?: boolean; // Make sure this is optional
  onClose: () => void;
  show: boolean;
}

interface EducationalLoansProps {
  write: boolean;
}

const EducationalLoans = ({ write }: EducationalLoansProps) => {

  const { isAdmin, isManager, isStaff } = useAuth();
  const { currentSubAgent } = useSubAgentAuth();

  const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);


  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const { leadId } = useParams<{ leadId: string }>();
  const [loans, setLoans] = useState<Loan[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isButtonCheckLoading, setIsButtonCheckLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [leadData, setLeadData] = useState<any>(null);
  const [companyId, setCompanyId] = useState<string>('');
  const [error, setError] = useState<string>('');

  const [newLoan, setNewLoan] = useState({
    id: '',
    name: '',
    number: '',
    email: '',
  });

  const [selectedStudent, setSelectedStudent] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isStudentDropdownOpen, setIsStudentDropdownOpen] = useState(false);
  const [students, setStudents] = useState<any[]>([]);
  const [isStudentLoading, setIsStudentLoading] = useState(false);

  // Fetch lead data and company ID on component load
  useEffect(() => {
    const fetchLeadData = async () => {

      try {
        setIsLoading(true);
        const currentUser = auth.currentUser;
        if (!currentUser) return;

        let companyId: string | undefined;
        // Get user document to get companyId
        if (currentSubAgent) {
          companyId = currentSubAgent.companyId;
        } else {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          companyId = userDoc.data()?.companyId;
        }
        if (!companyId) return;
        setCompanyId(companyId);

        // Get lead data
        if (companyId && leadId) {
          const leadDoc = await getDoc(doc(db, `companies/${companyId}/leads/${leadId}`));
          if (leadDoc.exists()) {
            setLeadData(leadDoc.data());
            setNewLoan({
              id: leadId,
              name: leadDoc.data().name || '',
              number: leadDoc.data().number || '',
              email: leadDoc.data().email || '',
            });
          }
        } else {
          setCompanyId(companyId);
          setIsButtonCheckLoading(false);
        }

        // Fetch existing loans
        fetchLoans(companyId);
      } catch (err) {
        console.error('Error fetching lead data:', err);
        setError('Failed to load lead data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchLeadData();
  }, [leadId]);

  // Fetch education loans for the company and lead directly from Firestore
  const fetchLoans = async (compId: string) => {
    if (!compId) return;

    try {
      setIsLoading(true);
      setIsButtonCheckLoading(true);
      console.log("compId", compId);
      console.log("leadId", leadId);

      // Get all education loans for the specific lead and company
      let loansQuery = query(
        collection(db, 'educationLoanLeads'),
        where('companyId', '==', compId),
        orderBy('createdAt', 'desc')
      );

      if (leadId) {
        loansQuery = query(
          collection(db, 'educationLoanLeads'),
          where('companyId', '==', compId),
          where('leadId', '==', leadId),
          orderBy('createdAt', 'desc')
        );
      }

      const loansSnapshot = await getDocs(loansQuery);

      // Get all external loan stage IDs
      const stageIds = new Set<string>();
      loansSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.externalLoanStageId) {
          stageIds.add(data.externalLoanStageId);
        }
      });

      // Get all the external loan stages
      let stagesMap: { [key: string]: any } = {};
      if (stageIds.size > 0) {
        const stageIdArray = Array.from(stageIds);

        // Fetch stages in batches of 10 (Firestore limitation for 'in' queries)
        for (let i = 0; i < stageIdArray.length; i += 10) {
          const batch = stageIdArray.slice(i, i + 10);
          const stagesQuery = query(
            collection(db, 'externalLoanStages'),
            where('__name__', 'in', batch)
          );

          const stagesSnapshot = await getDocs(stagesQuery);
          stagesSnapshot.docs.forEach(doc => {
            stagesMap[doc.id] = doc.data();
          });
        }
      }

      // Map the loan data
      const loansList = loansSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name,
          number: data.number,
          email: data.email || null,
          leadId: data.leadId,
          companyId: data.companyId,
          createdAt: data.createdAt,
          externalLoanStageId: data.externalLoanStageId,
          stageName: data.externalLoanStageId ?
            (stagesMap[data.externalLoanStageId]?.externalLoanStage || 'Unknown') :
            'Not assigned',
          canInitiate: data.externalLoanStageId ?
            (stagesMap[data.externalLoanStageId]?.canInitiate || false) :
            false
        };
      });

      setLoans(loansList);
    } catch (err) {
      console.error('Error fetching loans:', err);
      setError('Failed to load loans');
    } finally {
      setIsLoading(false);
      setIsButtonCheckLoading(false);
    }
  };

  const toggleDropdown = (dropdown: string, forceOpen?: boolean) => {
    if (dropdown === 'student') {
      setIsStudentDropdownOpen((prev) => forceOpen !== undefined ? forceOpen : !prev);
    }
  }

  // student search
  const handleStudentSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setSelectedStudent(value);
    setSearchTerm(value);

    // toogle student dropdown
    if (!isStudentDropdownOpen) {
      toggleDropdown("student", true);
    }


    if (value.length < 3) {
      setStudents([]);
      return;
    }

    setIsStudentLoading(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      let companyId: string | undefined;
      // Get user document to get companyId
      if (currentSubAgent) {
        companyId = currentSubAgent.companyId;
      } else {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        companyId = userDoc.data()?.companyId;
      }
      if (!companyId) return;

      let studentsQuery = query(
        collection(db, `companies/${companyId}/leads`),
        where('name', '>=', value),
        where('name', '<=', value + '\uf8ff'),
        orderBy('name')
      );

      if (currentSubAgent) {
        studentsQuery = query(
          collection(db, `companies/${currentSubAgent.companyId}/leads`),
          where('name', '>=', value),
          where('name', '<=', value + '\uf8ff'),
          where('subAgentId', '==', currentSubAgent.uid),
          orderBy('name')
        );
      } else if (isManager && currentScreen === 'Manager') {
        const usersQuery = query(
          collection(db, "users"),
          where("companyId", "==", companyId),
          where("managerIds", "array-contains", currentUser?.uid)
        );

        const usersSnapshot = await getDocs(usersQuery);
        const userIds = usersSnapshot.docs.map(doc => doc.id);
        studentsQuery = query(
          collection(db, `companies/${companyId}/leads`),
          where('name', '>=', value),
          where('name', '<=', value + '\uf8ff'),
          where('assignedTo', 'in', userIds),
          orderBy('name')
        );
      }


      const studentsSnapshot = await getDocs(studentsQuery);
      const studentList = studentsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));

      setStudents(studentList);
    } catch (error) {
      console.error("Error fetching students:", error);
    } finally {
      setIsStudentLoading(false);
    }
  };

  // Determine if the initiate loan button should be visible
  const shouldShowInitiateButton = () => {

    if (!leadId) {
      return true;
    }

    // Case 1: If there are no loans, show the button (first time loan initiation)
    if (leadId && loans.length === 0) {
      return true;
    }

    // Case 2: Check if all existing loans have canInitiate set to true
    return leadId && loans.every(loan => loan.canInitiate === true);
  };

  const handleInitiateLoan = () => {
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsStudentDropdownOpen(false);
    setSelectedStudent('');
    setSearchTerm('');
    setStudents([]);
    setIsStudentLoading(false);
    toggleDropdown("student", false);
    // Reset the new loan state
    setIsPopupOpen(false);
    setNewLoan({
      id: '',
      name: '',
      number: '',
      email: ''
    });
  };

  const handleAddLoan = async () => {
    // Validate form
    if (!newLoan.name || !newLoan.number) {
      alert('Please fill in all required fields');
      return;
    }

    if (!companyId) {
      alert('Company information is missing');
      return;
    }

    if (!leadId && !newLoan.id) {
      alert('Lead ID is missing');
      return;
    }

    try {
      setIsLoading(true);
      setIsSubmitting(true);

      // Check if the loan already exists
      const existingLoan = loans.find(loan => loan.name === newLoan.name && loan.number === newLoan.number);
      if (existingLoan) {
        setToastMessage('Error :Loan already exists for this student');
        setShowToast(true);
        setTimeout(() => {
          setShowToast(false);
        }, 3000);
        return;
      }

      // Call the initiateLoan function
      const initiateLoan = httpsCallable(functions, 'initiateLoan');
      const result = await initiateLoan({
        name: newLoan.name,
        number: newLoan.number,
        email: newLoan.email, // Include email in the function call
        leadId: leadId ?? newLoan.id,
        companyId: companyId
      });

      const data = result.data as { success: boolean, message: string };
      if (data.success) {
        // Refresh the loans list
        fetchLoans(companyId);
        closePopup();
      } else {
        alert('Failed to initiate loan');
      }
    } catch (err) {
      console.error('Error initiating loan:', err);
      alert('An error occurred while initiating the loan');
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-[#02385E] font-Montserrat">Educational Loan Services</h1>
        {write && (
          <>
            {isButtonCheckLoading ? (
              <button
                disabled
                className="px-4 py-2 text-white font-semibold rounded-full flex items-center gap-2"
                style={{ background: '#888888' }}  >
                <span style={{ fontFamily: 'Montserrat', fontSize: '20px', fontWeight: '500', lineHeight: '24.38px', }}>Checking...
                </span>
                <div className="w-4 h-4 ml-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              </button>
            ) : shouldShowInitiateButton() && (
              <button
                onClick={handleInitiateLoan}
                className="px-4 py-2 text-white font-semibold rounded-full flex items-center gap-2"
                style={{ background: 'linear-gradient(180deg, #FFA782 0%, #FC583D 100%)', }}  >
                <span style={{ fontFamily: 'Montserrat', fontSize: '20px', fontWeight: '500', lineHeight: '24.38px', }}>Initiate Loan
                </span>
                <img src={`${process.env.PUBLIC_URL}/images/desktop/staffadd.svg`} alt="Add Staff Icon" className="w-4 h-4 ml-2" />
              </button>
            )}
          </>
        )}
      </div>

      <div className="rounded-xl overflow-hidden bg-white shadow-lg">
        <div className="overflow-x-auto relative">
          <table className="min-w-full table-auto border-collapse">
            <thead>
              <tr className="bg-gradient-to-r from-[#02385E] to-[#02385E]/90">
                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">Loan Stage</th>
                {/* <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">Actions</th> */}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={4} className="px-4 py-4 text-center">Loading loans...</td>
                </tr>
              ) : loans.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-4 text-center">No loans found</td>
                </tr>
              ) : loans.map((loan, index) => (
                <tr key={loan.id} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                  <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                    <div className="truncate text-left" title={loan.name}>{loan.name}</div>
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                    <div className="truncate text-left" title={loan.number}>{loan.number}</div>
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                    <div className="truncate text-left" title={loan.email || ''}>{loan.email || '-'}</div>
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                    <span className={`inline-block min-w-[80px] px-2 py-1 rounded-full ${loan.stageName === 'Completed' ? 'bg-green-100 text-green-800' :
                      loan.stageName === 'Loan Request Initiated' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                      {loan.stageName}
                    </span>
                  </td>
                  {/* <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                    <div className="flex gap-4">
                      <img
                        src={`${process.env.PUBLIC_URL}/images/desktop/lucide-view.svg`}
                        alt="View Details"
                        className="w-5 h-5 cursor-pointer"
                        onClick={() => console.log('View loan details', loan.id)}
                      />
                      <img
                        src={`${process.env.PUBLIC_URL}/images/desktop/deleteicon.svg`}
                        alt="Delete Icon"
                        className="w-5 h-5 cursor-pointer"
                        onClick={() => console.log('Delete loan', loan.id)}
                      />
                    </div>
                  </td> */}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Loan Popup */}
      {isPopupOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-3xl w-[90%] md:w-[70%] lg:w-[50%] relative">
            <div className="top-0 left-0 right-0 h-[40px] flex items-center justify-between rounded-tl-[12px] rounded-tr-[12px] p-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-b from-[#00A6D9] to-[#3483BE] flex items-center justify-center">
                  <img src={`${process.env.PUBLIC_URL}/images/desktop/ph_student.svg`} alt="Student Icon" />
                </div>
                <h2 className="text-xl font-semibold text-black">Add Loan Application</h2>
              </div>
              <div className="text-right">
                <button onClick={closePopup} className="absolute top-4 right-4 font-semibold text-3xl" style={{ background: 'linear-gradient(270deg, #A7C8E7 0%, #00A6D9 100%)', WebkitBackgroundClip: 'text', color: 'transparent', }}>
                  <img src={`${process.env.PUBLIC_URL}/images/desktop/close-btn.svg`} alt="Close" className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 mt-8">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name <span className="text-red-500">*</span></label>
                {leadId ? (
                  <input
                    type="text"
                    placeholder="Applicant Name"
                    value={newLoan.name}
                    onChange={(e) => setNewLoan({ ...newLoan, name: e.target.value })}
                    className="border p-2 w-full mb-2"
                    style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                  />
                ) : (
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search for Student"
                      value={searchTerm || selectedStudent}
                      onChange={handleStudentSearch}
                      onClick={(e) => {
                        // Don't toggle the dropdown when clicking on the input field
                        // This prevents accidental closing while typing/searching
                        e.stopPropagation();
                        if (!isStudentDropdownOpen) {
                          toggleDropdown("student", true);
                        }
                      }}
                      className="border p-2 w-full mb-2 cursor-pointer"
                      style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                    />

                    {isStudentDropdownOpen && (
                      <div className="absolute z-10 bg-white border border-gray-300 rounded-lg shadow-lg mt-1 w-full max-h-[200px] overflow-y-auto">
                        {isStudentLoading ? (
                          <div className="absolute top-0 right-0 mt-2 mr-2">
                            <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        ) : students.length > 0 ? (
                          students.map((student) => (
                            <div
                              key={student.id}
                              className="p-2 hover:bg-gray-100 cursor-pointer"
                              onClick={() => {
                                setNewLoan({ ...newLoan, id: student.id, name: student.name, number: student.number, email: student.email });
                                setSelectedStudent(student.name);
                                setSearchTerm('');
                                setIsStudentDropdownOpen(false);
                              }}
                            >
                              {student.name}
                            </div>
                          )))
                          : searchTerm.length < 3 ? (
                            <div className="p-2 text-gray-500">Type at least 3 characters to search</div>
                          ) : (
                            <div className="p-2 text-gray-500">No students found</div>
                          )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone Number <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  placeholder="Phone Number"
                  value={newLoan.number}
                  onChange={(e) => setNewLoan({ ...newLoan, number: e.target.value })}
                  className="border p-2 w-full mb-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  placeholder="Email Address"
                  value={newLoan.email}
                  onChange={(e) => setNewLoan({ ...newLoan, email: e.target.value })}
                  className="border p-2 w-full mb-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                />
              </div>
            </div>

            <div className="mt-8 flex justify-end">
              <GradientSubmitButton
                type="button"
                onClick={handleAddLoan}
                loading={isSubmitting}
                label="Submit"
                loadingLabel="Submitting..."
                disabled={isSubmitting}
                // className="px-8 py-2 rounded-3xl min-w-[120px]"
              />
            </div>
          </div>
        </div>
      )} 
      {/*  */}

      {showToast && (
        <Toast
          message={toastMessage}
          isError={
            toastMessage.includes('Error') ||
            toastMessage.includes('Cannot') ||
            toastMessage.includes('Failed')
          }
          show={showToast}
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  );
};

export default EducationalLoans;